import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {DataTable} from "primereact/datatable";
import {Row} from "primereact/row";
import {TabPanel, TabView} from "primereact/tabview";
import {Tooltip} from "primereact/tooltip";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared/i18n";
import {formatPrice, formatTypedValue, parseGuiType} from "@reva/shared/number-format";
import {SEGMENT} from "@reva/shared/reva-spec";
import {splitMultilineString} from "@reva/shared/string-utils";

import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import ErrorIndicator from "../../component/ErrorIndicator";
import I18nErrorMessage from "../../component/I18nErrorMessage";
import LoadingIndicator from "../../component/LoadingIndicator";
import DescriptionIcon from "../../component/paramInput/DescriptionIcon";
import {InputGrossFloorAreaEditPanel} from "../../component/paramInput/InputGrossFloorArea";
import {InputSpecialPropertyFeaturesEditPanel} from "../../component/paramInput/InputSpecialPropertyFeatures";
import {useParameterValue} from "../../component/paramInput/paramHooks";
import {TabHeaderWithTooltip} from "../../component/TabHeaderWithTooltip";
import ValueLendingValueInfo from "../../component/ValueLendingValueInfo";
import {useLanguage, useRevaTranslation, useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {useCheckPermissionAdmin} from "../../hooks/permissionHooks";
import {
    selectInitialized as documentsInitialized,
    selectLoading as selectDocumentsLoading,
    selectSingleDocument,
} from "../../redux/documentsSlice";
import {downloadDocumentAsync, loadDocumentsAsync} from "../../redux/documentsThunk";
import {
    selectModernizationDegreeError,
    selectQualityFurnishingsError,
    selectQualityFurnishingsPreview,
} from "../../redux/objectInformationSlice";
import {selectAssociatedOutputParamOutputCurried} from "../../redux/objectParametersSelectors";
import {
    selectAllInspectionDerivedParams,
    selectAllInspectionParams,
    selectSegment,
    selectSingleInspectionParam,
    selectSingleParam,
} from "../../redux/objectParametersSlice";
import {DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL} from "../../redux/pdfConstants";
import {selectPersistenceLoading, selectRevaId} from "../../redux/persistenceSlice";
import {
    selectQualityFurnishingsCraftsMatrixModel,
    selectQualityFurnishingsCraftToMatrixKeyCurried,
} from "../../redux/specSelectors";
import {selectSpec, selectLoading as selectSpecLoading} from "../../redux/specSlice";
import {selectSwotNegatives, selectSwotPositives} from "../../redux/swotSelectors";
import {selectEffectiveIndicationData, selectFlattenedIndicationData} from "../../redux/valueIndicationSelectors";
import {selectCombinedIndicationData} from "../../redux/valueIndicationSlice";
import {selectHasValueOrders} from "../../redux/valueOrdersSlice";
import {aggregateIndicationTableData} from "../../util/indicationTableUtil";
import {countLeadingWhitespace} from "../../util/stringUtil";
import ErrorMessageContainer from "./ErrorMessageContainer";
import {buildModernizationDegreeRows, ObjectInputModernizationDegree} from "./ObjectInputModernizationDegree";
import {
    craftLabel,
    foldQualityFurnishingsWeights,
    ObjectInputQualityFurnishings,
    QUALITY_FURNISHINGS_ORDER,
    weightsFromMatrixEntry,
} from "./ObjectInputQualityFurnishings";
import {ObjectInputSwotAnalyse as InspectionInputBlockSwotAnalyse} from "./ObjectInputSwotAnalyse";
import {CollapsableSection, ObjectInputsBlock, SectionTitle} from "./ObjectInputs";
import {ObjectOutputGrid} from "./ObjectOutputGrid";
import PersistenceFinalMessage from "./PersistenceFinalMessage";
import SnapshotMessage from "./SnapshotMessage";

/**
 * Input Section Title for Inspection Tab.
 * @param {string} title section title
 * @param {string|undefined} [appreciationSection] optional section value for appreciation
 * @param {boolean} firstSection flag whether this is the first section in the column
 * @param {string|null} [collapseKey] optional: when provided, show collapse/expand icon and make header a clickable button
 * @return {Element}
 */
function InspectionTitle({title, appreciationSection, firstSection, collapseKey}) {
    return (
        <SectionTitle
            title={title}
            appreciationTab="inspection"
            appreciationSection={appreciationSection}
            firstSection={firstSection}
            collapseKey={collapseKey}
        />
    );
}

function getObjectInspectionColumn(spec) {
    const columns = spec?.objectInputs.find((page) => page.key === "object/inspection")?.columns;
    return columns?.find((c) => c.key === "objectinspection_left");
}

const joinInfos = (...infos) => infos.filter(Boolean).join("\n") || undefined;

function InspectionDescriptionIcon({param, extraInfo}) {
    const inspectionData = useSelector(selectAllInspectionParams);
    const selectLang = useSelectByCurrentLanguage();
    const infoDe = inspectionData?.[`${param}_info_de`];
    const infoEn = inspectionData?.[`${param}_info`];
    const description = selectLang(infoDe, infoEn)?.replaceAll(";", "\n");
    return <DescriptionIcon description={joinInfos(description, extraInfo)} hasAssociatedOutputParam={false} />;
}

function OutputInfoIcon({param, recalculated, info, extraInfo}) {
    if (recalculated)
        return <DescriptionIcon description={joinInfos(info, extraInfo)} hasAssociatedOutputParam={false} />;
    else return <InspectionDescriptionIcon param={param} extraInfo={extraInfo} />;
}

/**
 * Labels der Zeilen, deren Modernisierungsjahr nach dem Jahr des Wertermittlungsstichtags liegt. Ohne Stichtag leer.
 * @param {{label: string, year: number|null}[]} rows Zeilen der Modernisierungstabelle
 * @param {string|null|undefined} valuationDate Wertermittlungsstichtag
 */
export function modernizationsAfterValuation(rows, valuationDate) {
    if (!valuationDate) return [];
    const valuationYear = new Date(valuationDate).getFullYear();
    return rows.filter((row) => row.year != null && row.year > valuationYear).map((row) => row.label);
}

function InspectionOutputRaw() {
    const isAdmin = useCheckPermissionAdmin();
    const inspectionData = useSelector(selectAllInspectionParams);

    if (!isAdmin) return null;
    return <pre style={{whiteSpace: "pre-wrap"}}>{JSON.stringify(inspectionData ?? {}, null, 2)}</pre>;
}

function InspectionOutputData() {
    const inspectionData = useSelector(selectAllInspectionParams);
    const specLoading = useSelector(selectSpecLoading);
    const persistenceLoading = useSelector(selectPersistenceLoading);

    if (specLoading || persistenceLoading) return null;
    return <ObjectOutputGrid outputData={inspectionData} fullsize={false} />;
}

function InspectionOutputBlockModernizationDegree() {
    const inspectionData = useSelector(selectAllInspectionParams);
    const inspectionDerivedData = useSelector(selectAllInspectionDerivedParams);
    const spec = useSelector(selectSpec);
    const segment = useSelector(selectSegment);
    const {parameterValues: valuationDateValues} = useParameterValue("valuation_date", "AVM");
    const {t} = useTranslation();

    const tk = "object.tab_inspection.output.degree_of_modernization_table";
    const inputParams = spec?.apiParams?.MODERNIZATION_DEGREE?.[segment] || {};
    const getParamValue = (param) => inspectionDerivedData[param] ?? inspectionData[param];

    const rows = buildModernizationDegreeRows(inputParams).map((row) => ({
        label: t(`${tk}.${row.labelKey}`),
        score: getParamValue(row.scoreParam),
        year: getParamValue(row.yearParam),
    }));
    const valuationDate = valuationDateValues?.activeParameterValue;
    const afterValuation = modernizationsAfterValuation(rows, valuationDate);
    const afterValuationInfo =
        afterValuation.length > 0
            ? t(`${tk}.year_after_valuation`, {
                  year: new Date(valuationDate).getFullYear(),
                  crafts: afterValuation.join(", "),
              })
            : undefined;

    const footerRow = (labelKey, param, extraInfo) => {
        const value = getParamValue(param);
        const weighted = inspectionDerivedData[`${param}_weighted`];
        const recalculated = inspectionDerivedData[param] != null || weighted != null;
        const info =
            weighted != null && value != null && weighted !== value
                ? t(`${tk}.${param}_deviation`, {value: weighted})
                : undefined;

        return (
            <Row>
                <Column
                    footer={
                        <>
                            {t(`${tk}.${labelKey}`)}{" "}
                            <OutputInfoIcon
                                param={param}
                                recalculated={recalculated}
                                info={info}
                                extraInfo={extraInfo}
                            />
                        </>
                    }
                />
                <Column footer={""} />
                <Column footer={value} footerClassName={"p-align-right"} />
            </Row>
        );
    };

    const footerGroup = (
        <>
            {footerRow("footer_nhk_degree", "nhk_degree_of_modernization")}
            {footerRow("footer_year", "year_of_modernization", afterValuationInfo)}
        </>
    );

    return (
        <DataTable value={rows} size={"small"} stripedRows footerColumnGroup={footerGroup}>
            <Column header={""} footer={""} field={"label"} />
            <Column
                header={t(`${tk}.col_score`)}
                field={"score"}
                className="p-align-right"
                headerClassName="p-align-right"
            />
            <Column
                header={t(`${tk}.col_year`)}
                field={"year"}
                className="p-align-right"
                headerClassName="p-align-right"
                footerClassName={"p-align-right"}
            />
        </DataTable>
    );
}

function InspectionOutputBlockQualityFurnishing() {
    const inspectionData = useSelector(selectAllInspectionParams);
    const inspectionDerivedData = useSelector(selectAllInspectionDerivedParams);
    const preview = useSelector(selectQualityFurnishingsPreview);
    const spec = useSelector(selectSpec);
    const segment = useSelector(selectSegment);
    const craftToMatrixKey = useSelector(selectQualityFurnishingsCraftToMatrixKeyCurried(segment));
    const craftsMatrixModel = useSelector(selectQualityFurnishingsCraftsMatrixModel);
    const {t, language} = useRevaTranslation();
    const tk = "object.tab_inspection.output.quality_furnishings_table";

    const guiType = parseGuiType("DOUBLE(2)", null, null, null);
    const formattedValue = (value) => formatTypedValue(language, value, guiType);
    // Vorschau (#19507) nur als letzter Fallback: gespeicherte Werte und Besichtigung haben Vorrang.
    const getParamValue = (param) => inspectionDerivedData?.[param] ?? inspectionData?.[param] ?? preview?.[param];
    const paramToValue = (param) => formattedValue(getParamValue(param));
    const paramToName = (param) => {
        const paramDef = spec?.apiParams.VALUE[segment]?.[param];
        if (!paramDef) return null;
        return selectByLanguage(language, paramDef.nameDe, paramDef.nameEn);
    };
    const inputParams = spec?.apiParams?.QUALITY_FURNISHINGS?.[segment] || {};
    const isMFHWGH_K = segment === SEGMENT.MFHWGH_K;
    const fieldIndices = isMFHWGH_K ? [2, 3, 4] : [0, 1, 2, 3, 4];
    const levelLabels = fieldIndices.map((i) => String(i + 1));
    let fullyFilledWithWeights = true;
    let allWeightsValid = true;
    const allInfos = [];

    const rows = Object.keys(inputParams)
        .map((specKey) => {
            const param = specKey.replace("_weights", "");
            const label = craftLabel(inputParams[specKey], language);
            const matrixProperty = craftsMatrixModel[craftToMatrixKey[param]];
            const description = selectByLanguage(
                language,
                matrixProperty?.descriptionDe,
                matrixProperty?.descriptionEn,
            );
            fullyFilledWithWeights = fullyFilledWithWeights && getParamValue(specKey) != null;
            const value = formattedValue(inspectionData[param]);
            const element = getParamValue("nhk_crafts_matrix")?.[craftToMatrixKey[param]];
            // ohne Gewichte die Vorbelegung, mit der die AVM gerechnet hat (#19511)
            const weights = getParamValue(specKey) ?? weightsFromMatrixEntry(element);
            const nhkCraftsMatrixEntry = element?.[element.length - 1];
            const factor = nhkCraftsMatrixEntry?.craft_weight;
            const craftWeightedSum = formattedValue(nhkCraftsMatrixEntry?.craft_weighted_sum);
            const portion = factor ? factor * 100 : null;
            const rowBase = {param, label, description, value, portion, craftWeightedSum};
            allWeightsValid = allWeightsValid && (!isMFHWGH_K || (!weights?.[0] && !weights?.[1]));
            allInfos.push(
                selectByLanguage(language, inspectionData[`${param}_info_de`], inspectionData[`${param}_info`]),
            );

            const foldedWeights = weights ? foldQualityFurnishingsWeights(weights, fieldIndices[0]) : [];
            return fieldIndices.reduce((akk, fieldIndex) => {
                akk[`weight_${fieldIndex}`] =
                    foldedWeights[fieldIndex] > 0 ? formattedValue(foldedWeights[fieldIndex]) : null;
                return akk;
            }, rowBase);
        })
        .sort((a, b) => QUALITY_FURNISHINGS_ORDER.indexOf(a.param) - QUALITY_FURNISHINGS_ORDER.indexOf(b.param));

    const nhkRaw = getParamValue("nhk_crafts_based_furnishings");
    const qualityContRaw = getParamValue("quality_furnishings_continuous");
    const recalculated =
        nhkRaw !== inspectionData["nhk_crafts_based_furnishings"] ||
        qualityContRaw !== inspectionData["quality_furnishings_continuous"];
    const qualityFurnishingsConInfo =
        nhkRaw != null && qualityContRaw != null && nhkRaw !== qualityContRaw
            ? t(`${tk}.quality_furnishings_continuous_deviation`)
            : undefined;

    const qualityFurnishingsInfos = [];
    if (!fullyFilledWithWeights) qualityFurnishingsInfos.push(t(`${tk}.default_values_set`));
    if (!allWeightsValid) qualityFurnishingsInfos.push(t(`${tk}.weights_projected`));
    let showSingleInfos = allInfos.filter((i) => i != null).every((i) => i === allInfos[0]);
    if (!recalculated && showSingleInfos) qualityFurnishingsInfos.push(allInfos[0]);
    showSingleInfos = recalculated || showSingleInfos;
    const qualityFurnishingsInfo = qualityFurnishingsInfos.filter((i) => i != null).join(", ");

    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header="" colSpan={1} />
                <Column
                    header={t(`${tk}.col_standard`)}
                    colSpan={levelLabels.length}
                    headerClassName={"p-align-center"}
                />
                <Column header={t(`${tk}.col_weighting`)} colSpan={1} headerClassName={"p-align-right"} />
                <Column header={t(`${tk}.col_weighted_sum`)} colSpan={1} headerClassName={"p-align-right"} />
            </Row>
            <Row>
                <Column header="" />
                {levelLabels.map((label) => (
                    <Column key={label} header={label} headerClassName={"p-align-center"} />
                ))}
                <Column header="" />
                <Column header="" />
            </Row>
        </ColumnGroup>
    );

    const footerNhkFurnishings = (
        <>
            {t(`${tk}.nhk_crafts_based_furnishings`)}
            <OutputInfoIcon param={"nhk_crafts_based_furnishings"} recalculated={true} info={qualityFurnishingsInfo} />
        </>
    );
    const footerFurnshingsContinuous = (
        <>
            {paramToName("quality_furnishings_continuous")}
            <OutputInfoIcon
                param={"quality_furnishings_continuous"}
                recalculated={recalculated}
                info={qualityFurnishingsConInfo}
            />
        </>
    );
    const footerGroup = (
        <ColumnGroup>
            <Row>
                <Column footer={footerNhkFurnishings} colSpan={1} />
                <Column footer="" colSpan={isMFHWGH_K ? 4 : 6} />
                <Column
                    footer={paramToValue("nhk_crafts_based_furnishings")}
                    colSpan={1}
                    footerClassName={"p-align-right"}
                />
            </Row>
            <Row>
                <Column footer={footerFurnshingsContinuous} colSpan={1} />
                <Column footer="" colSpan={isMFHWGH_K ? 4 : 6} />
                <Column
                    footer={paramToValue("quality_furnishings_continuous")}
                    colSpan={1}
                    footerClassName={"p-align-right"}
                />
            </Row>
        </ColumnGroup>
    );

    const titleColumn = (row) => (
        <>
            <span className="quality-furnishings-output-label-cell" data-pr-tooltip={row.description}>
                {row.label}
            </span>{" "}
            <InspectionDescriptionIcon param={showSingleInfos === true ? null : row.param} />
        </>
    );

    return (
        <>
            <Tooltip
                key={`tooltip-${recalculated}`}
                target=".quality-furnishings-output-label-cell"
                position="right"
                className="custom-tooltip nhk-tooltip"
            />
            <DataTable
                key={`table-${recalculated}`}
                value={rows}
                size="small"
                stripedRows
                headerColumnGroup={headerGroup}
                footerColumnGroup={footerGroup}
            >
                <Column body={titleColumn} />
                {fieldIndices.map((fieldIndex) => (
                    <Column key={fieldIndex} field={`weight_${fieldIndex}`} className="p-align-center" />
                ))}
                <Column field="portion" className="p-align-right" headerClassName="p-align-right" />
                <Column field="craftWeightedSum" className="p-align-right" headerClassName="p-align-right" />
            </DataTable>
        </>
    );
}

function InspectionOutputPanel() {
    const {t} = useTranslation();
    const spec = useSelector(selectSpec);
    const isAdmin = useCheckPermissionAdmin();
    const segment = useSelector(selectSegment);
    const inspectionData = useSelector(selectAllInspectionParams);
    const showAllTabs = Object.keys(inspectionData).length !== 0;

    const column = getObjectInspectionColumn(spec);
    if (!column?.blocks) {
        return <ErrorIndicator i18nKey="object.errorLoadingSpec" />;
    }
    return (
        <TabView className="registerOutputTabView sticky top-0" scrollable>
            <TabPanel key="outputDataPanel" header={<TabHeaderWithTooltip i18nPrefix="object.tab_inspection.output" />}>
                <InspectionOutputBlockOrderData />
                {segment !== SEGMENT.WHG_K && segment !== SEGMENT.GRST_K && (
                    <>
                        <InspectionTitle
                            title={t("object.tab_inspection.output.building_category")}
                            collapseKey="inspection_output_building_category"
                        />
                        <CollapsableSection collapseKey="inspection_output_building_category">
                            <InspectionOutputBlockBuildingCategory />
                        </CollapsableSection>
                    </>
                )}
                {segment !== SEGMENT.GRST_K && (
                    <>
                        <InspectionTitle
                            title={t("object.tab_inspection.output.gross_floor_area")}
                            collapseKey="inspection_output_gross_floor_area"
                        />
                        <CollapsableSection collapseKey="inspection_output_gross_floor_area">
                            <InspectionOutputBlockGrossFloorArea />
                        </CollapsableSection>
                        {segment !== SEGMENT.WHG_K && (
                            <>
                                <InspectionTitle
                                    title={t("object.tab_inspection.output.quality_furnishings")}
                                    collapseKey="inspection_output_quality_furnishings"
                                />
                                <CollapsableSection collapseKey="inspection_output_quality_furnishings">
                                    <InspectionOutputBlockQualityFurnishing />
                                </CollapsableSection>
                            </>
                        )}
                        <InspectionTitle
                            title={t("object.tab_inspection.output.degree_of_modernization")}
                            collapseKey="inspection_output_degree_of_modernization"
                        />
                        <CollapsableSection collapseKey="inspection_output_degree_of_modernization">
                            <InspectionOutputBlockModernizationDegree />
                        </CollapsableSection>
                        <InspectionTitle
                            title={t("object.tab_inspection.output.property_age")}
                            collapseKey="inspection_output_property_age"
                        />
                        <CollapsableSection collapseKey="inspection_output_property_age">
                            <InspectionOutputBlockPropertyAge />
                        </CollapsableSection>
                    </>
                )}
                <InspectionTitle
                    title={t("object.tab_inspection.input.spf_title")}
                    collapseKey="inspection_output_spf"
                />
                <CollapsableSection collapseKey="inspection_output_spf">
                    <InspectionOutputBlockSpecialPropertyFeatures />
                </CollapsableSection>
                <InspectionTitle
                    title={t("object.tab_inspection.output.swot_analyse")}
                    collapseKey="inspection_output_swot"
                />
                <CollapsableSection collapseKey="inspection_output_swot">
                    <InspectionOutputBlockSwotAnalyse />
                </CollapsableSection>
                <InspectionOutputBlockNotice />
            </TabPanel>
            <TabPanel
                key="documentPanel"
                visible={showAllTabs}
                header={<span className="tabViewTitle">{t("object.tab_inspection.output.document_title")}</span>}
            >
                <InspectionOutputDocument />
            </TabPanel>
            <TabPanel
                key="besiData"
                visible={showAllTabs}
                header={<span className="tabViewTitle">{t("object.tab_inspection.output.data_title")}</span>}
            >
                <InspectionOutputData />
            </TabPanel>
            <TabPanel
                key="besiRaw"
                visible={isAdmin && showAllTabs}
                header={<span className="tabViewTitle">BesiConverterDaten</span>}
            >
                <InspectionOutputRaw />
            </TabPanel>
        </TabView>
    );
}

function InspectionOutputBlockOrderData() {
    const indicationData = useSelector(selectFlattenedIndicationData);
    const inspectionData = useSelector(selectAllInspectionParams);
    const {t} = useTranslation();
    const rows = [
        "order_value_customer_name",
        "order_value_client",
        "order_value_external_number",
        "property_inspection_type",
        "property_inspection_date",
        "property_inspection_surveyor",
    ].map((param) => {
        return {
            param,
            label: t(`object.tab_inspection.output.order_data_table.${param}`),
            value: indicationData?.[param] ?? inspectionData[param],
        };
    });
    // Nur anzeigen, wenn tatsächlich Auftragsdaten vorliegen (z.B. Besichtigungsprotokoll importiert).
    if (rows.every((row) => row.value == null || row.value === "")) return null;

    return (
        <>
            <InspectionTitle title={t("order.orderData")} collapseKey="inspection_output_order_data" />
            <CollapsableSection collapseKey="inspection_output_order_data">
                <DataTable size={"small"} value={rows} stripedRows showHeaders={false}>
                    <Column field={"label"} />
                    <Column field={"value"} headerClassName={"p-align-right"} className={"p-align-right"} />
                </DataTable>
            </CollapsableSection>
        </>
    );
}

function InspectionOutputBlockBuildingCategory() {
    const spec = useSelector(selectSpec);
    const activeValue = useSelector(selectAssociatedOutputParamOutputCurried("building_category_nhk"));
    const selectLang = useSelectByCurrentLanguage();
    const {t} = useTranslation();
    const param = spec?.apiParams.output?.["building_category_nhk_cat"];
    if (!param?.categories) return null;

    const paramLabel = spec?.apiParams.output?.["building_category_nhk"];
    const value = activeValue ?? null;
    const valueLabel =
        activeValue?.hasConflict || false
            ? t("object.error.unresolvedConflict")
            : param.categories?.find((c) => value && value === c.shortNameDe)?.titleDe || null;
    const keyLabel = selectLang(paramLabel?.nameDe, paramLabel?.nameEn);
    const rows = [
        {param: "building_category_nhk", label: keyLabel, value},
        {param: null, label: `${keyLabel} ${selectLang("Beschreibung", "description")}`, value: valueLabel},
    ];
    const title = (row) => (
        <>
            {row.label}
            <InspectionDescriptionIcon param={row.param} />
        </>
    );
    return (
        <DataTable size={"small"} value={rows} stripedRows showHeaders={false}>
            <Column field={"label"} body={title} />
            <Column field={"value"} headerClassName={"p-align-right"} className={"p-align-right"} />
        </DataTable>
    );
}

function TitleRowCell({value}) {
    const title = value.title;
    const leadingWhiteSpaces = countLeadingWhitespace(title);

    return (
        <span
            style={{
                marginLeft: `${leadingWhiteSpaces}rem`,
                display: "block",
            }}
        >
            {title.trimStart()}
        </span>
    );
}

function InspectionOutputBlockPropertyAge() {
    const spec = useSelector(selectSpec);
    const indicationData = useSelector(selectCombinedIndicationData);
    const {t, language} = useRevaTranslation();
    if (!indicationData) return null;
    const pageEntry = Object.entries(spec.pages).find(
        ([key, p]) => !!indicationData[key] && !!p.rowGroups.find((g) => g.group === "alter"),
    );
    if (!pageEntry) return null;
    const pageKey = pageEntry[0];
    const pageData = pageEntry[1];
    const relevantRows = pageData.rows.filter((r) => r.group === "alter");
    const columns = [pageData.columns[0]];
    const filteredRevaSpec = {...spec, pages: {[pageKey]: {...pageData, rows: relevantRows}}};
    const renderedRows = indicationData
        ? aggregateIndicationTableData(language, filteredRevaSpec, pageKey, indicationData[pageKey])
        : [];
    return (
        <DataTable
            value={renderedRows}
            emptyMessage={t("value.emptyMessage")}
            tableClassName="indication-table"
            size="small"
            stripedRows
            showHeaders={false}
        >
            <Column
                field="title"
                style={{
                    minWidth: "16em",
                    //width: `${titleColumnWidth}%`,
                }}
                className="title-column"
                header=""
                body={(data) => <TitleRowCell value={data} />}
            />
            {columns.map((col) => (
                <Column
                    key={col.field}
                    field={col.field}
                    className="value-column p-align-right"
                    headerClassName="p-align-right pr-8"
                    style={{
                        minWidth: "4em",
                        //width: `${columnWidth}%`,
                    }}
                    body={(rowData) => {
                        return rowData[col.field]?.value;
                    }}
                />
            ))}
        </DataTable>
    );
}

function InspectionOutputBlockGrossFloorArea() {
    const language = useLanguage();
    const {parameterValues: modelValues} = useParameterValue(DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL, "DOC_PROPERTY");
    const grossFloorAreaTotal = useSelector((state) =>
        selectSingleParam(state, "gross_floor_area", "INSPECTION_DERIVED"),
    );
    const rows = modelValues?.activeParameterValue ? [...modelValues.activeParameterValue.items] : [];
    const formatter = (value, unit) => formatTypedValue(language, value, parseGuiType("DOUBLE(0,2)", unit, unit, null));

    return (
        <div className={"pb-4"}>
            <DataTable value={rows} size={"small"} stripedRows>
                <Column field={"description"} header={""}></Column>
                <Column
                    header={"Faktor"}
                    body={(d) => formatter(d.factor, null)}
                    className="p-align-right"
                    headerClassName="p-align-right"
                />
                <Column
                    field={"length"}
                    header={"Länge"}
                    body={(d) => formatter(d.length, "m")}
                    className="p-align-right"
                    headerClassName="p-align-right"
                />
                <Column
                    field={"width"}
                    header={"Breite"}
                    body={(d) => formatter(d.width, "m")}
                    className="p-align-right"
                    headerClassName="p-align-right"
                />
                <Column
                    field={"space"}
                    header={"Fläche"}
                    body={(d) => formatter(d.space, "m²")}
                    className="p-align-right"
                    headerClassName="p-align-right"
                />
                <Column
                    field={"gross_floor_area"}
                    header={"BGF"}
                    body={(d) => formatter(d.gross_floor_area, "m²")}
                    className="p-align-right"
                    headerClassName="p-align-right"
                    footerClassName={"p-align-right"}
                    footer={`Σ ${formatter(grossFloorAreaTotal, "m²")}`}
                />
            </DataTable>
        </div>
    );
}

function InspectionOutputBlockSpecialPropertyFeatures() {
    const {t, language} = useRevaTranslation();
    const indicationData = useSelector(selectEffectiveIndicationData);

    const spfData = indicationData?.special_property_feature_list?.[0];
    const marketData = spfData?.special_property_feature_list || [];
    const lendingData = spfData?.special_property_feature_list_lending || [];

    const marketByName = Object.fromEntries(marketData.map((f) => [f.feature_de, f]));
    const lendingByName = Object.fromEntries(lendingData.map((f) => [f.feature_de, f]));
    const allNames = [...new Set([...Object.keys(marketByName), ...Object.keys(lendingByName)])];

    const spfRows = allNames.map((featureKey) => {
        const marketItem = marketByName[featureKey];
        const lendingItem = lendingByName[featureKey];
        return {
            name: selectByLanguage(language, featureKey, marketItem?.feature_en ?? lendingItem?.feature_en),
            rawMarket: marketItem?.value ?? 0,
            rawLending: lendingItem?.value ?? 0,
            market: formatPrice(marketItem?.value, language),
            lending: formatPrice(lendingItem?.value, language),
        };
    });

    const marketSum = spfRows.reduce((acc, row) => acc + row.rawMarket, 0);
    const lendingSum = spfRows.reduce((acc, row) => acc + row.rawLending, 0);
    const footerGroup = (
        <ColumnGroup>
            <Row>
                <Column footer="Σ" />
                <Column footer={formatPrice(marketSum, language)} footerClassName="p-align-right" />
                <Column footer={formatPrice(lendingSum, language)} footerClassName="p-align-right" />
            </Row>
        </ColumnGroup>
    );

    return (
        <div className={"pb-4"}>
            <DataTable value={spfRows} size={"small"} stripedRows footerColumnGroup={footerGroup}>
                <Column field={"name"} header={t("object.tab_inspection.input.spf_name")}></Column>
                <Column
                    field={"market"}
                    header={t("object.tab_inspection.input.spf_market")}
                    className="p-align-right"
                    headerClassName="p-align-right"
                />
                <Column
                    field={"lending"}
                    header={t("object.tab_inspection.input.spf_lending")}
                    className="p-align-right"
                    headerClassName="p-align-right"
                />
            </DataTable>
        </div>
    );
}

function InspectionOutputBlockSwotAnalyse() {
    const {t} = useTranslation();
    const positives = useSelector(selectSwotPositives);
    const negatives = useSelector(selectSwotNegatives);
    const rows = [];
    const maxIndex = Math.max(positives?.items?.length ?? 0, negatives?.items?.length ?? 0);
    for (let i = 0; i < maxIndex; i++) {
        const positive = positives?.items?.[i]?.description ?? "";
        const negative = negatives?.items?.[i]?.description ?? "";
        rows.push({positive, negative});
    }
    return (
        <DataTable value={rows} size={"small"} stripedRows>
            <Column field={"positive"} header={t("object.tab_inspection.output.swot_analyse_positives")}></Column>
            <Column field={"negative"} header={t("object.tab_inspection.output.swot_analyse_negatives")}></Column>
        </DataTable>
    );
}

function InspectionOutputBlockNotice() {
    const selectLang = useSelectByCurrentLanguage();
    const infoEn = useSelector((state) => selectSingleInspectionParam(state, "info_hint"));
    const infoDe = useSelector((state) => selectSingleInspectionParam(state, "info_hint_de"));
    const infoText = selectLang(infoDe, infoEn);
    const rows = splitMultilineString(infoText).map((line) => ({label: line}));
    if (rows.length === 0) return null;

    return (
        <div>
            <SectionTitle title="Hinweise" collapseKey="inspection_output_notice" />
            <CollapsableSection collapseKey="inspection_output_notice">
                <DataTable value={rows} size={"small"} showHeaders={false} stripedRows className={"text-xs"}>
                    <Column field={"label"}></Column>
                </DataTable>
            </CollapsableSection>
        </div>
    );
}

function InspectionOutputBlockWarning() {
    const infoEn = useSelector((state) => selectSingleInspectionParam(state, "info_hint"));
    const infoDe = useSelector((state) => selectSingleInspectionParam(state, "info_hint_de"));
    if (!infoDe || !infoEn) return null;
    const errorsDe = infoDe.split(" / ").filter((e) => /Kein Gebäudetyp|Keine relevanten Eingabedaten/.test(e));
    const errorsEn = infoEn
        .split(" / ")
        .filter((e) => /No relevant input data available|No building type information found/.test(e));
    const errors = [];
    for (let i = 0; i < errorsDe.length && i < errorsEn.length; i++) {
        errors.push({
            error: "object.tab_inspection.output.error_missing_data",
            errorArgs: {errorData: {error: errorsEn[i], error_de: errorsDe[i]}},
        });
    }
    if (errors.length === 0) return null;
    return (
        <div className={"col-span-12"}>
            {errors.map((e) => (
                <I18nErrorMessage key={e.errorArgs.errorData.error} enabled={true} errorObject={e} />
            ))}
        </div>
    );
}

//TODO Make sure it s the chronologically first Besichtigungsbericht
function InspectionOutputDocument() {
    const revaId = useSelector(selectRevaId);
    const hasValueOrders = useSelector(selectHasValueOrders);
    const isDocumentsInitialized = useSelector(documentsInitialized);
    const isDocumentsLoading = useSelector(selectDocumentsLoading);
    const document = useSelector((state) => selectSingleDocument(state, "Besichtigungsbericht"));
    const dispatch = useDispatch();
    useEffect(() => {
        if (revaId && !isDocumentsInitialized && !isDocumentsLoading) dispatch(loadDocumentsAsync());
    }, [revaId, isDocumentsInitialized, isDocumentsLoading, dispatch]);
    const [inspectionDocument, setInspectionDocument] = useState(undefined);
    useEffect(() => {
        dispatch(downloadDocumentAsync(document))
            .then((blob) => setInspectionDocument({url: URL.createObjectURL(blob), name: document.filename}))
            .catch(console.error);
    }, [document, dispatch]);
    if (!hasValueOrders || !document) return null;
    if ("pdf" === document.extension)
        return (
            <iframe
                title={inspectionDocument?.name}
                src={inspectionDocument?.url}
                width="100%"
                style={{height: "90vh"}}
            />
        );
    if (document.extension && /png|jpe?g|avif/.test(document.extension.toLowerCase()))
        return (
            <img
                alt={inspectionDocument?.name}
                src={inspectionDocument?.url}
                width="100%"
                style={{marginTop: "1rem"}}
            />
        );
}

function InspectionInputPanel() {
    const spec = useSelector(selectSpec);
    const {t} = useTranslation();
    const segment = useSelector(selectSegment);

    if (!spec?.objectInputs) return null;
    const inspectionColumn = getObjectInspectionColumn(spec);

    if (!inspectionColumn?.blocks) return null;
    const blocks = inspectionColumn.blocks;
    return (
        <>
            {segment !== SEGMENT.WHG_K && segment !== SEGMENT.GRST_K && (
                <>
                    <InspectionTitle
                        title={t("object.tab_inspection.input.building_category")}
                        firstSection={true}
                        collapseKey="inspection_building_category"
                    />
                    <CollapsableSection collapseKey="inspection_building_category">
                        <InspectionInputBlock block={blocks[0]} />
                    </CollapsableSection>
                </>
            )}
            {segment !== SEGMENT.GRST_K && (
                <>
                    <InspectionTitle
                        title={t("object.tab_inspection.input.gross_floor_area")}
                        appreciationSection="bgf"
                        collapseKey="inspection_bgf"
                        firstSection={segment === SEGMENT.WHG_K}
                    />
                    <CollapsableSection collapseKey="inspection_bgf">
                        <InputGrossFloorAreaEditPanel parameterType={"INSPECTION_DERIVED"} />
                    </CollapsableSection>
                    <InspectionTitle
                        title={t("object.tab_inspection.input.quality_furnishings")}
                        collapseKey="inspection_quality_furnishings"
                    />
                    <CollapsableSection collapseKey="inspection_quality_furnishings">
                        <InspectionInputQualityFurnishings block={blocks[2]} />
                    </CollapsableSection>
                    <InspectionTitle
                        title={t("object.tab_inspection.input.degree_of_modernization")}
                        collapseKey="inspection_degree_of_modernization"
                    />
                    <CollapsableSection collapseKey="inspection_degree_of_modernization">
                        <ObjectInputModernizationDegree />
                    </CollapsableSection>
                    <InspectionTitle
                        title={t("object.tab_inspection.input.property_age")}
                        appreciationSection="modernisierungsgrad_baualter"
                        collapseKey="inspection_property_age_and_modernization"
                    />
                    <CollapsableSection collapseKey="inspection_property_age_and_modernization">
                        <InspectionInputBlock block={blocks[3]} />
                    </CollapsableSection>
                </>
            )}
            <InspectionTitle
                title={t("object.tab_inspection.input.spf_title")}
                appreciationSection="spf"
                collapseKey="inspection_special_property_features"
                firstSection={segment === SEGMENT.GRST_K}
            />
            <CollapsableSection collapseKey="inspection_special_property_features">
                <InputSpecialPropertyFeaturesEditPanel />
            </CollapsableSection>
            <InspectionTitle
                title={t("object.tab_inspection.input.swot_analyse")}
                appreciationSection="swot"
                collapseKey="inspection_swot"
            />
            <CollapsableSection collapseKey="inspection_swot">
                <InspectionInputBlockSwotAnalyse
                    header={t("object.tab_inspection.input.swot_analyse_positives")}
                    parameter={"inspection_swot_positives"}
                />
                <InspectionInputBlockSwotAnalyse
                    header={t("object.tab_inspection.input.swot_analyse_negatives")}
                    parameter={"inspection_swot_negatives"}
                />
            </CollapsableSection>
        </>
    );
}

function InspectionInputBlock({block}) {
    return <ObjectInputsBlock block={block} />;
}

function InspectionInputQualityFurnishings({block}) {
    if (block.key !== "objectinspection_furnishings_classification") return null;

    return (
        <div>
            <ObjectInputQualityFurnishings />
            <ObjectInputsBlock block={block} />
        </div>
    );
}

function TabObjectInspectionContent() {
    const specLoading = useSelector(selectSpecLoading);
    const persistenceLoading = useSelector(selectPersistenceLoading);

    const loading = specLoading || persistenceLoading;

    if (loading) return <LoadingIndicator loading={loading} className="h-full" />;

    return (
        <div className="grid grid-cols-12 gap-4">
            <InspectionOutputBlockWarning />
            <div className="col-span-6">
                <InspectionInputPanel />
            </div>
            <div className="col-span-6">
                <InspectionOutputPanel />
            </div>
        </div>
    );
}

/**
 * @returns {React.JSX.Element}
 */
function TabObjectInspection() {
    const modernizationDegreeError = useSelector(selectModernizationDegreeError);
    const qualityFurnishingsError = useSelector(selectQualityFurnishingsError);

    return (
        <div id="inspectionPanelTitle">
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <AppreciationTabInputText page="object" tab="inspection" />
            <ValueLendingValueInfo />
            <ErrorMessageContainer />
            <I18nErrorMessage errorObject={modernizationDegreeError} className="mb-4 w-full" />
            <I18nErrorMessage errorObject={qualityFurnishingsError} className="mb-4 w-full" />
            <TabObjectInspectionContent />
        </div>
    );
}

export default TabObjectInspection;
